// The interactive speech session (composer dictation), driven by an injected
// policy (./policy.ts, addendum A-2).
//
//   tap        claim the mic; start the capture and mint in parallel inside the
//              gesture; frames go to the spool and the link from the first one
//   live       partials and finals reach the store (the read-only live text) only
//   finish     (✓ or Enter) stop the capture, send the tail and `stop`, wait for
//              `committed` and insert the text at the caret. It is never sent.
//   cancel     (✕ or Esc) drop the audio and the text
//   failure    the audio stays in the spool; the notice offers Retry / Discard
//   interrupt  (page hidden, device lost, meeting took the mic) the text is
//              committed and held for an explicit Insert or Discard
//
// There is no silence stop: only the policy's safety stop after the server's
// `idleStopSec` without speech (with a countdown) and the length cap (with a
// warning) end a dictation automatically, and both insert, never send.
import type {
  CaptureHandle,
  CaptureLifecycle,
  CaptureOptions,
  MicOwner,
  PcmFrame,
  SpeechMintResponse,
  SpeechSpool,
  SpoolMeta,
  SpoolSession,
} from '../contracts';
import { isVoiceActivity, levelFromRms } from '../capture/level';
import { speechIssue, speechIssueFor, type SpeechIssue } from '../errors';
import { FRAME_MS, SAMPLES_PER_MS } from '../transport/protocol';
import { DictationLink, type LinkDeps } from './link';
import { DICTATION_POLICY, type InteractivePolicy } from './policy';
import { ACTIVE_PHASES, upsertSegment, type SpeechState, type SpeechStore } from './store';

export const DEFAULT_LIMITS = { maxSessionSec: 900, idleStopSec: 20 } as const;
/** A spool session still `capturing` is someone's live dictation until it is this old. */
const STALE_CAPTURE_MS = 2 * 60_000;

export type DictationDeps = Omit<LinkDeps, 'frames'> & {
  store: SpeechStore;
  policy?: InteractivePolicy;
  micOwner: MicOwner;
  spool: SpeechSpool;
  startCapture(options: CaptureOptions): Promise<CaptureHandle>;
  newId(): string;
  /** The signed-in user; spool sessions are scoped to it. */
  owner(): string | null;
  threadId?(): string | null;
  /** Limits known before the mint answers (GET /api/v1/speech/config). */
  limits?(): { maxSessionSec: number; idleStopSec: number } | null;
  /** Insert at the caret; false when no composer can take the text. */
  insert(text: string): boolean;
};

export type DictationController = {
  start(): Promise<void>;
  /** Insert: commit and insert at the caret. */
  stop(): Promise<void>;
  cancel(): Promise<void>;
  retry(): Promise<void>;
  discard(): Promise<void>;
  insertPending(): void;
  dismiss(): void;
  /** Surface a spooled dictation this owner left behind (reload, crash). */
  restoreKept(): Promise<void>;
  /** The composer is going away: stop and keep the audio. */
  suspend(): void;
};

type Run = {
  id: string;
  threadId?: string;
  capture: Promise<CaptureHandle | null> | null;
  spool: Promise<SpoolSession>;
  spoolSession: SpoolSession | null;
  early: PcmFrame[];
  link: DictationLink;
  release: (() => void) | null;
  limits: { maxSessionSec: number; idleStopSec: number };
  frameRms: number;
  level: number;
  durationMs: number;
  lastVoiceMs: number;
  stopping: boolean;
  ended: boolean;
};

type StopOptions = { hold: boolean; issue?: SpeechIssue | null };

export function createDictationController(deps: DictationDeps): DictationController {
  const policy = deps.policy ?? DICTATION_POLICY;
  const store = deps.store;
  const set = (patch: Partial<SpeechState>) => store.setState(patch);
  const get = () => store.getState();
  let run: Run | null = null;

  const releaseMic = (r: Run) => {
    r.release?.();
    r.release = null;
  };

  const stopCapture = async (r: Run) =>
    (await r.capture?.catch(() => null))?.stop().catch(() => null) ?? null;

  const withSpool = async (r: Run, action: (session: SpoolSession) => Promise<unknown>) => {
    const session = await r.spool.catch(() => null);
    if (session) await action(session).catch(() => {});
  };

  const endRun = (r: Run) => {
    r.ended = true;
    releaseMic(r);
    if (run === r) run = null;
  };

  // ── Outcomes ─────────────────────────────────────────────────────────────

  async function finish(r: Run, text: string, options: StopOptions) {
    if (r.ended) return;
    endRun(r);
    if (!text.trim()) {
      await deps.spool.remove(r.id);
      set({ phase: 'idle', segments: [], notice: null, level: 0, error: speechIssue('no_audio') });
      return;
    }
    if (options.hold || !deps.insert(text)) {
      await withSpool(r, (session) => session.setState('committed'));
      set({
        phase: 'pending-insert',
        pendingText: text,
        keptSessionId: r.id,
        error: options.issue ?? null,
        notice: null,
        level: 0,
      });
      return;
    }
    await deps.spool.remove(r.id);
    set({
      phase: 'idle',
      segments: [],
      notice: null,
      level: 0,
      error: null,
      inserted: get().inserted + 1,
    });
    await restoreKept();
  }

  async function keep(r: Run, issue: SpeechIssue) {
    if (r.ended) return;
    endRun(r);
    r.link.close();
    await stopCapture(r);
    if (!issue.kept) {
      await deps.spool.remove(r.id);
      set({ phase: 'idle', segments: [], notice: null, level: 0, error: issue });
      return;
    }
    await withSpool(r, (session) => session.setState('failed', { errorCode: issue.code }));
    set({ phase: 'kept', keptSessionId: r.id, error: issue, notice: null, level: 0 });
  }

  async function stopRun(r: Run, options: StopOptions) {
    if (r.stopping || r.ended) return;
    r.stopping = true;
    set({ phase: 'committing', notice: null });
    r.link.hold();
    const stopped = await stopCapture(r);
    if (r.ended) return;
    await withSpool(r, (session) => session.flush());
    const lastSeq = stopped?.lastSeq ?? r.spoolSession?.meta().lastSeq ?? -1;
    try {
      await finish(r, await r.link.commit(lastSeq), options);
    } catch (error) {
      await keep(r, (error as SpeechIssue)?.key ? (error as SpeechIssue) : speechIssueFor(error));
    }
  }

  // ── Policy on every frame ────────────────────────────────────────────────

  function applyPolicy(r: Run) {
    if (r.stopping || r.ended) return;
    const maxMs = r.limits.maxSessionSec * 1000;
    const idleMs = r.limits.idleStopSec * 1000;
    const silentMs = r.durationMs - r.lastVoiceMs;
    const autoStop = { hold: policy.autoStop === 'hold' };
    if (r.durationMs >= maxMs - FRAME_MS) return void stopRun(r, autoStop);
    if (policy.idleStop && idleMs > 0 && silentMs >= idleMs) return void stopRun(r, autoStop);
    const idleLeft = idleMs - silentMs;
    const maxLeft = maxMs - r.durationMs;
    const notice =
      policy.idleStop && idleMs > 0 && idleLeft <= policy.idleCountdownMs
        ? ({ kind: 'idle-stop', seconds: Math.max(1, Math.ceil(idleLeft / 1000)) } as const)
        : maxLeft <= policy.warnBeforeMs
          ? ({ kind: 'limit', seconds: Math.max(1, Math.ceil(maxLeft / 1000)) } as const)
          : null;
    const current = get().notice;
    if (current?.kind !== notice?.kind || current?.seconds !== notice?.seconds) set({ notice });
  }

  function onFrame(r: Run, frame: PcmFrame) {
    if (r.ended || r !== run) return;
    if (r.spoolSession) r.spoolSession.append(frame);
    else r.early.push(frame);
    r.link.push(frame);
    r.durationMs = (frame.sampleOffset + frame.samples) / SAMPLES_PER_MS;
    if (isVoiceActivity(r.frameRms, policy.voiceActivityLevel)) r.lastVoiceMs = r.durationMs;
    r.level = levelFromRms(r.frameRms, r.level);
    set({ level: r.level });
    applyPolicy(r);
  }

  const INTERRUPTS: Partial<Record<CaptureLifecycle, string | null>> = {
    hidden: 'hidden',
    'mic-ended': 'device_lost',
    'context-suspended': null,
  };

  function onLifecycle(r: Run, event: CaptureLifecycle) {
    if (!(event in INTERRUPTS) || policy.onInterrupt !== 'stop-hold') return;
    if (r.stopping || r.ended) return;
    const code = INTERRUPTS[event];
    void stopRun(r, { hold: true, issue: code ? speechIssue(code) : null });
  }

  // ── The link's events ────────────────────────────────────────────────────

  function linkFor(r: Run, capturedThrough?: number) {
    const frames = async function* (from: number, to: number) {
      const session = await r.spool;
      yield* session.frames(from, to);
    };
    return new DictationLink(
      { capturedThrough, threadId: r.threadId },
      { ...deps, frames },
      {
        onMinted: (mint: SpeechMintResponse) => {
          r.limits = mint.limits;
        },
        onReady: () => {
          if (!r.stopping && !r.ended) set({ phase: 'live' });
        },
        onPartial: (key, message) => {
          if (r.ended) return;
          set({
            segments: upsertSegment(get().segments, {
              key,
              text: message.text,
              stable: Math.max(0, Math.min(message.stable, message.text.length)),
              final: false,
              startMs: message.startMs,
              endMs: message.endMs,
            }),
          });
        },
        onFinal: (key, message) => {
          if (r.ended) return;
          set({
            segments: upsertSegment(get().segments, {
              key,
              text: message.text,
              stable: message.text.length,
              final: true,
              startMs: message.startMs,
              endMs: message.endMs,
            }),
          });
        },
        onDegraded: () => set({ degraded: true }),
        onReconnecting: (offline) => {
          if (r.ended) return;
          set({
            segments: get().segments.filter((segment) => segment.final),
            ...(r.stopping ? {} : { phase: offline ? 'offline' : 'reconnecting' }),
          });
        },
        onUnsolicitedCommit: (text) => {
          r.stopping = true;
          void stopCapture(r).then(() => finish(r, text, { hold: true }));
        },
        onFailure: (issue) => void keep(r, issue),
      },
    );
  }

  function newRun(id: string, spool: Promise<SpoolSession>, threadId?: string): Run {
    const r: Run = {
      id,
      threadId,
      capture: null,
      spool,
      spoolSession: null,
      early: [],
      link: undefined as unknown as DictationLink,
      release: null,
      limits: deps.limits?.() ?? { ...DEFAULT_LIMITS },
      frameRms: 0,
      level: 0,
      durationMs: 0,
      lastVoiceMs: 0,
      stopping: false,
      ended: false,
    };
    void spool.then(
      (session) => {
        r.spoolSession = session;
        for (const frame of r.early.splice(0)) session.append(frame);
      },
      () => {},
    );
    return r;
  }

  // ── Public API ───────────────────────────────────────────────────────────

  async function start() {
    // A committed text waits for Insert or Discard first; it is never dropped.
    if (run || get().phase === 'pending-insert') return;
    const owner = deps.owner();
    if (!owner) return void set({ error: speechIssue('not_authenticated') });
    let r: Run | null = null;
    const release = deps.micOwner.claim(policy.micKind, () => {
      if (r) void stopRun(r, { hold: true });
    });
    if (!release) return void set({ error: speechIssue('mic_held_by_meeting') });
    const id = deps.newId();
    const threadId = deps.threadId?.() ?? undefined;
    const spool = deps.spool.open({
      id,
      owner,
      kind: 'dictation',
      threadId,
      createdAt: deps.now(),
      state: 'capturing',
      lastSeq: -1,
      samples: 0,
    });
    const current = newRun(id, spool, threadId);
    r = current;
    current.release = release;
    run = current;
    // An older kept recording stays in the spool and is offered again afterwards.
    set({
      phase: 'starting',
      keptSessionId: null,
      segments: [],
      error: null,
      notice: null,
      degraded: false,
      level: 0,
    });
    // Inside the gesture: the capture (AudioContext resume, permission prompt)
    // and the mint start synchronously, before anything awaits.
    current.capture = deps.startCapture({
      sources: ['mic'],
      purpose: 'dictation',
      onFrame: (frame) => onFrame(current, frame),
      onLevel: (rms) => {
        current.frameRms = rms;
      },
      onLifecycle: (event) => onLifecycle(current, event),
    });
    current.link = linkFor(current);
    current.link.start();
    try {
      const handle = await current.capture;
      if (current.ended) await handle?.stop();
    } catch (error) {
      if (current.ended) return;
      endRun(current);
      current.link.cancel();
      await deps.spool.remove(id);
      set({ phase: 'idle', segments: [], level: 0, error: speechIssueFor(error) });
    }
  }

  async function retry() {
    const { keptSessionId } = get();
    const owner = deps.owner();
    if (run || !keptSessionId || !owner) return;
    const meta = (await deps.spool.list(owner, 'dictation')).find(
      (item) => item.id === keptSessionId,
    );
    if (!meta) return void set({ phase: 'idle', keptSessionId: null, error: null });
    const r = newRun(meta.id, deps.spool.open(meta), meta.threadId);
    run = r;
    r.stopping = true;
    set({ phase: 'committing', error: null, notice: null, segments: [], degraded: false });
    r.link = linkFor(r, meta.lastSeq);
    r.link.start();
    try {
      await finish(r, await r.link.commit(meta.lastSeq), { hold: false });
    } catch (error) {
      await keep(r, (error as SpeechIssue)?.key ? (error as SpeechIssue) : speechIssueFor(error));
    }
  }

  async function cancel() {
    const r = run;
    if (!r) return;
    endRun(r);
    r.link.cancel();
    set({ phase: 'idle', segments: [], notice: null, level: 0, error: null });
    await stopCapture(r);
    await deps.spool.remove(r.id);
    await restoreKept();
  }

  async function discard() {
    const { keptSessionId } = get();
    set({ phase: 'idle', keptSessionId: null, pendingText: null, error: null, segments: [] });
    if (keptSessionId) await deps.spool.remove(keptSessionId);
    await restoreKept();
  }

  function insertPending() {
    const { pendingText, keptSessionId } = get();
    if (!pendingText || !deps.insert(pendingText)) return;
    set({
      phase: 'idle',
      pendingText: null,
      keptSessionId: null,
      error: null,
      segments: [],
      inserted: get().inserted + 1,
    });
    if (keptSessionId) void deps.spool.remove(keptSessionId);
  }

  async function restoreKept() {
    const owner = deps.owner();
    if (!owner || run || get().keptSessionId) return;
    const now = deps.now();
    const candidates = (await deps.spool.list(owner, 'dictation').catch(() => [] as SpoolMeta[]))
      .filter((meta) => meta.state !== 'capturing' || now - meta.updatedAt > STALE_CAPTURE_MS)
      .sort((a, b) => b.updatedAt - a.updatedAt);
    const found = candidates[0];
    if (!found || run || get().keptSessionId || get().phase !== 'idle') return;
    set({ phase: 'kept', keptSessionId: found.id, error: speechIssue('interrupted') });
  }

  function suspend() {
    const r = run;
    if (!r) return;
    endRun(r);
    r.link.close();
    void stopCapture(r);
    void withSpool(r, (session) => session.setState('failed', { errorCode: 'interrupted' }));
  }

  return {
    start,
    stop: async () => {
      if (run && ACTIVE_PHASES.has(get().phase)) await stopRun(run, { hold: false });
    },
    cancel,
    retry,
    discard,
    insertPending,
    dismiss: () => {
      if (!get().keptSessionId) set({ error: null });
    },
    restoreKept,
    suspend,
  };
}
